import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { useAppStore } from '~/lib/store'
import type { MarkdownTask, TaskStatusOverride } from '~/lib/api'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

export const Route = createFileRoute('/tasks')({
    component: TasksPage,
})

type TaskStatus = 'todo' | 'in_progress' | 'done'

const columns: { id: TaskStatus; title: string; color: string; bgColor: string }[] = [
    { id: 'todo', title: 'To Do', color: 'bg-slate-500', bgColor: 'bg-slate-500/10' },
    { id: 'in_progress', title: 'In Progress', color: 'bg-blue-500', bgColor: 'bg-blue-500/10' },
    { id: 'done', title: 'Done', color: 'bg-green-500', bgColor: 'bg-green-500/10' },
]

// Merge markdown task with status override
function getEffectiveTask(task: MarkdownTask, statuses: Record<string, TaskStatusOverride>): MarkdownTask & { assignee?: string } {
    const override = statuses[task.id]
    if (!override) return task
    return {
        ...task,
        status: override.status || task.status,
        assignee: override.assignee,
    }
}

function TasksPage() {
    const { markdownTasks, taskStatuses, fetchMarkdownTasks, isAuthenticated, fetchDocTree } = useAppStore()
    const [isLoading, setIsLoading] = useState(true)
    const [viewMode, setViewMode] = useState<'board' | 'list'>('board')
    const [selectedPhase, setSelectedPhase] = useState<{ project: string; phase: string; tasks: (MarkdownTask & { assignee?: string })[] } | null>(null)
    const [selectedTask, setSelectedTask] = useState<(MarkdownTask & { assignee?: string }) | null>(null)

    // Filters
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedProject, setSelectedProject] = useState<string>('all')
    const [selectedAssignee, setSelectedAssignee] = useState<string>('all')

    useEffect(() => {
        Promise.all([
            fetchMarkdownTasks(),
            fetchDocTree()
        ]).finally(() => setIsLoading(false))
    }, [fetchMarkdownTasks, fetchDocTree])

    if (isLoading) {
        return (
            <div className="p-6 h-full flex items-center justify-center animate-fade-in">
                <div className="text-slate-400">Loading tasks...</div>
            </div>
        )
    }

    if (!markdownTasks || markdownTasks.tasks.length === 0) {
        return (
            <div className="p-6 h-full flex flex-col animate-fade-in">
                <header className="mb-6">
                    <h1 className="text-2xl font-bold text-white mb-1">Task Board</h1>
                    <p className="text-slate-400 text-sm">
                        No tasks found. Add tasks using <code className="bg-slate-700 px-1.5 py-0.5 rounded text-xs">- [ ] Task text</code> in markdown files.
                    </p>
                </header>
            </div>
        )
    }

    // Apply status overrides to tasks
    const effectiveTasks = markdownTasks.tasks.map(t => getEffectiveTask(t, taskStatuses))

    // Filter tasks
    const filteredTasks = effectiveTasks.filter(task => {
        const matchesSearch = searchQuery === '' ||
            task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (task.description?.toLowerCase().includes(searchQuery.toLowerCase()))

        const matchesProject = selectedProject === 'all' || task.project === selectedProject
        const matchesAssignee = selectedAssignee === 'all' ||
            (selectedAssignee === 'unassigned' ? !task.assignee : task.assignee === selectedAssignee)

        return matchesSearch && matchesProject && matchesAssignee
    })

    // Get unique projects and assignees for filters
    const projects = Array.from(new Set(effectiveTasks.map(t => t.project || t.section))).sort()
    const assignees = Array.from(new Set(effectiveTasks.map(t => t.assignee).filter(Boolean))) as string[]

    // Recalculate totals based on effective statuses
    const totals = filteredTasks.reduce((acc, task) => ({
        total: acc.total + 1,
        completed: acc.completed + (task.status === 'done' ? 1 : 0),
        inProgress: acc.inProgress + (task.status === 'in_progress' ? 1 : 0),
    }), { total: 0, completed: 0, inProgress: 0 })

    const completionPercent = totals.total > 0 ? Math.round((totals.completed / totals.total) * 100) : 0

    return (
        <div className="p-6 h-full flex flex-col animate-fade-in">
            <header className="mb-6">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-white mb-1">Task Board</h1>
                        <p className="text-slate-400 text-sm">
                            Manage documentation tasks across projects.
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setViewMode('list')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                        >
                            📄 List
                        </button>
                        <button
                            onClick={() => setViewMode('board')}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${viewMode === 'board' ? 'bg-blue-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                        >
                            📋 Board
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <div className="flex-1 min-w-[200px] relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">🔍</span>
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-9 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                        />
                    </div>

                    <select
                        value={selectedProject}
                        onChange={(e) => setSelectedProject(e.target.value)}
                        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                    >
                        <option value="all">All Projects</option>
                        {projects.map(p => (
                            <option key={p} value={p}>{p}</option>
                        ))}
                    </select>

                    <select
                        value={selectedAssignee}
                        onChange={(e) => setSelectedAssignee(e.target.value)}
                        className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500 transition-colors"
                    >
                        <option value="all">All Assignees</option>
                        <option value="unassigned">Unassigned</option>
                        {assignees.map(a => (
                            <option key={a} value={a}>{a}</option>
                        ))}
                    </select>
                </div>

                {/* Overall Progress */}
                <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                    <div className="flex items-center gap-4">
                        <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-medium text-slate-300">Progress</span>
                                <span className="text-sm text-slate-400">
                                    <span className="text-green-400 font-bold">{totals.completed}</span> / {totals.total} completed
                                    {totals.inProgress > 0 && <span className="text-blue-400 ml-2">({totals.inProgress} in progress)</span>}
                                </span>
                            </div>
                            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-500"
                                    style={{ width: `${completionPercent}%` }}
                                />
                            </div>
                        </div>
                        <div className="text-2xl font-bold text-white w-12 text-right">
                            {completionPercent}%
                        </div>
                    </div>
                </div>
            </header>

            <div className="flex-1 min-h-0 flex gap-4">
                {/* Main Board/List View */}
                <div className={`flex-1 min-h-0 transition-all duration-300 ${selectedPhase ? 'w-1/2' : 'w-full'}`}>
                    {viewMode === 'list' ? (
                        <TaskListView
                            tasks={filteredTasks}
                            onTaskClick={setSelectedTask}
                        />
                    ) : (
                        <PhaseBoardView
                            tasks={filteredTasks}
                            onPhaseClick={setSelectedPhase}
                            canEdit={isAuthenticated}
                        />
                    )}
                </div>

                {/* Split Pane - Phase Detail */}
                {selectedPhase && (
                    <div className="w-1/2 min-h-0 bg-slate-900/50 rounded-2xl border border-slate-700/50 flex flex-col overflow-hidden animate-slide-in-right">
                        <PhaseDetailPane
                            phase={selectedPhase}
                            onClose={() => setSelectedPhase(null)}
                            onTaskClick={(task: MarkdownTask & { assignee?: string }) => setSelectedTask(task)}
                            selectedTask={selectedTask}
                        />
                    </div>
                )}
            </div>

            {selectedTask && (
                <TaskDetailModal task={selectedTask} onClose={() => setSelectedTask(null)} />
            )}
        </div>
    )
}

interface PhaseSummary {
    id: string
    project: string
    phase: string
    actor?: string
    status: TaskStatus
    tasks: (MarkdownTask & { assignee?: string })[]
    total: number
    completed: number
    inProgress: number
    percent: number
}

function PhaseBoardView({ tasks, onPhaseClick, canEdit }: {
    tasks: (MarkdownTask & { assignee?: string })[]
    onPhaseClick: (phase: { project: string; phase: string; tasks: (MarkdownTask & { assignee?: string })[] }) => void
    canEdit: boolean
}) {
    const { updateMarkdownTaskStatus } = useAppStore()
    const [draggedPhase, setDraggedPhase] = useState<string | null>(null)
    const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | null>(null)

    const handleDragStart = useCallback((e: React.DragEvent, phaseId: string) => {
        e.dataTransfer.setData('text/plain', phaseId)
        setDraggedPhase(phaseId)
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent, columnId: TaskStatus) => {
        e.preventDefault()
        setDragOverColumn(columnId)
    }, [])

    const handleDragLeave = useCallback(() => {
        setDragOverColumn(null)
    }, [])

    // Aggregate tasks into PhaseSummaries
    const phaseGroups = tasks.reduce((acc, task) => {
        const project = task.project || 'Root'
        const phase = task.phase || 'General'
        const key = `${project}:${phase}`

        if (!acc[key]) {
            acc[key] = {
                id: key,
                project,
                phase,
                actor: task.actor,
                tasks: [],
                total: 0,
                completed: 0,
                inProgress: 0,
                percent: 0,
                status: 'todo'
            }
        }

        acc[key].tasks.push(task)
        acc[key].total++
        if (task.status === 'done') acc[key].completed++
        if (task.status === 'in_progress') acc[key].inProgress++

        return acc
    }, {} as Record<string, PhaseSummary>)

    // Calculate aggregate status for each phase
    const phaseSummaries = Object.values(phaseGroups).map(summary => {
        summary.percent = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0

        // Status logic
        if (summary.completed === summary.total && summary.total > 0) {
            summary.status = 'done'
        } else if (summary.inProgress > 0 || (summary.completed > 0 && summary.completed < summary.total)) {
            summary.status = 'in_progress'
        } else {
            summary.status = 'todo'
        }

        return summary
    })

    const handleDrop = useCallback(async (e: React.DragEvent, newStatus: TaskStatus) => {
        e.preventDefault()
        const phaseId = e.dataTransfer.getData('text/plain')
        setDraggedPhase(null)
        setDragOverColumn(null)

        if (!canEdit) return

        const phase = phaseSummaries.find(p => p.id === phaseId)
        if (!phase || phase.status === newStatus) return

        // Bulk update tasks in this phase
        const confirmResult = window.confirm(`Change all ${phase.tasks.length} tasks in "${phase.phase}" to ${newStatus}?`)
        if (!confirmResult) return

        try {
            await Promise.all(phase.tasks.map(t =>
                updateMarkdownTaskStatus(t.id, { status: newStatus })
            ))
        } catch (error) {
            console.error('Failed to update phase status:', error)
        }
    }, [phaseSummaries, canEdit, updateMarkdownTaskStatus])

    return (
        <div className="h-full grid grid-cols-1 md:grid-cols-3 gap-6 min-h-0">
            {columns.map((column) => {
                const columnPhases = phaseSummaries.filter(p => p.status === column.id)
                const isDragOver = dragOverColumn === column.id

                return (
                    <div
                        key={column.id}
                        className={`rounded-2xl border flex flex-col min-h-0 shadow-inner transition-all duration-200 ${isDragOver
                            ? `${column.bgColor} border-2 border-dashed border-slate-500`
                            : 'bg-slate-800/20 border-slate-700/50'
                            }`}
                        onDragOver={(e) => handleDragOver(e, column.id)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.id)}
                    >
                        <div className="px-5 py-3 border-b border-slate-700/50 flex items-center gap-2 sticky top-0 bg-slate-900/40 backdrop-blur-md rounded-t-2xl z-10">
                            <span className={`w-2 h-2 rounded-full ${column.color}`} />
                            <span className="font-bold text-white text-sm uppercase tracking-wider">{column.title}</span>
                            <span className="text-xs text-slate-500 ml-auto bg-slate-700/50 px-2 py-0.5 rounded-full font-mono">
                                {columnPhases.length}
                            </span>
                        </div>
                        <div className="flex-1 p-4 space-y-4 overflow-y-auto">
                            {columnPhases.map((phase) => (
                                <PhaseCard
                                    key={phase.id}
                                    phase={phase}
                                    isDragging={draggedPhase === phase.id}
                                    canDrag={canEdit}
                                    onDragStart={handleDragStart}
                                    onDragEnd={() => setDraggedPhase(null)}
                                    onClick={() => onPhaseClick({ project: phase.project, phase: phase.phase, tasks: phase.tasks })}
                                />
                            ))}
                            {columnPhases.length === 0 && (
                                <div className="text-center py-12 text-slate-700 text-sm italic font-medium">
                                    {isDragOver ? 'Drop here' : 'No phases here'}
                                </div>
                            )}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function PhaseCard({ phase, isDragging, canDrag, onDragStart, onDragEnd, onClick }: {
    phase: PhaseSummary
    isDragging?: boolean
    canDrag?: boolean
    onDragStart?: (e: React.DragEvent, id: string) => void
    onDragEnd?: () => void
    onClick: () => void
}) {
    const statusCol = columns.find(c => c.id === phase.status)

    return (
        <div
            draggable={canDrag}
            onDragStart={(e) => onDragStart?.(e, phase.id)}
            onDragEnd={onDragEnd}
            onClick={onClick}
            className={`group relative bg-slate-800/80 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-5 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-blue-500/10 active:scale-[0.98]
                ${canDrag ? 'hover:border-slate-500/50 cursor-grab active:cursor-grabbing' : 'hover:border-slate-600'}
                ${isDragging ? 'opacity-30 scale-95 rotate-1' : 'opacity-100'}
            `}
        >
            {/* Left Status Indicator Strip */}
            <div className={`absolute left-0 top-4 bottom-4 w-1.5 rounded-r-full ${statusCol?.color} opacity-70 group-hover:opacity-100 transition-opacity`} />

            <div className="pl-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] font-black text-slate-500 uppercase tracking-tighter opacity-70 truncate max-w-[100px]">
                        {phase.project}
                    </span>
                    {phase.actor && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                            {phase.actor}
                        </span>
                    )}
                    <span className="text-[10px] font-mono text-slate-600 bg-slate-900/50 px-1.5 py-0.5 rounded ml-auto">
                        {phase.total} tasks
                    </span>
                </div>

                <h3 className="text-base font-black text-slate-100 leading-tight group-hover:text-blue-400 transition-colors mb-4 line-clamp-2">
                    {phase.phase}
                </h3>

                <div className="space-y-3">
                    <div className="flex items-center justify-between text-[10px] font-bold">
                        <span className="text-slate-400">PROGRESS</span>
                        <span className={phase.percent === 100 ? 'text-green-500' : 'text-slate-300'}>
                            {phase.percent}%
                        </span>
                    </div>
                    <div className="h-1.5 bg-slate-900/50 rounded-full overflow-hidden border border-slate-700/30">
                        <div
                            className={`h-full transition-all duration-500 ${phase.percent === 100 ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.4)]' : 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]'}`}
                            style={{ width: `${phase.percent}%` }}
                        />
                    </div>

                    <div className="flex items-center gap-3 pt-2">
                        <div className="flex -space-x-1.5 overflow-hidden">
                            {/* Simple placeholders for assignees if needed, or just show counts */}
                            {Array.from(new Set(phase.tasks.map(t => t.assignee).filter(Boolean))).slice(0, 3).map((assignee, i) => (
                                <div key={i} className="inline-block h-5 w-5 rounded-full ring-2 ring-slate-800 bg-slate-700 flex items-center justify-center text-[8px] font-bold text-white uppercase" title={assignee}>
                                    {assignee?.charAt(0)}
                                </div>
                            ))}
                        </div>
                        <span className="text-[10px] text-slate-600 font-medium ml-auto uppercase tracking-widest">
                            View details →
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}


function PhaseDetailPane({ phase, onClose, onTaskClick, selectedTask }: {
    phase: { project: string; phase: string; tasks: (MarkdownTask & { assignee?: string })[] }
    onClose: () => void
    onTaskClick: (task: MarkdownTask & { assignee?: string }) => void
    selectedTask: (MarkdownTask & { assignee?: string }) | null
}) {
    const completedCount = phase.tasks.filter(t => t.status === 'done').length
    const percent = Math.round((completedCount / phase.tasks.length) * 100)

    return (
        <>
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between bg-slate-800/20 backdrop-blur-md">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-tighter">{phase.project}</span>
                        <span className="text-[10px] font-mono text-slate-600 bg-slate-900/50 px-1.5 py-0.5 rounded">{phase.tasks.length} tasks</span>
                    </div>
                    <h2 className="text-sm font-black text-white uppercase tracking-wider truncate">{phase.phase}</h2>
                </div>
                <button
                    onClick={onClose}
                    className="p-2 hover:bg-slate-700/50 rounded-lg text-slate-400 hover:text-white transition-colors"
                >
                    ✕
                </button>
            </div>

            {/* Task List */}
            <div className="flex-1 overflow-y-auto min-h-0 bg-slate-900/20">
                <div className="p-4 space-y-3">
                    {phase.tasks.map((task) => (
                        <div
                            key={task.id}
                            onClick={() => onTaskClick(task)}
                            className={`p-4 rounded-xl border transition-all cursor-pointer group ${selectedTask?.id === task.id
                                    ? 'bg-blue-600 border-blue-500 shadow-lg shadow-blue-500/20'
                                    : 'bg-slate-800/40 border-slate-700/50 hover:border-slate-600 hover:bg-slate-800/60'
                                }`}
                        >
                            <div className="flex items-start gap-3">
                                <span className="text-base shrink-0">
                                    {task.status === 'done' ? '✅' : task.status === 'in_progress' ? '🔄' : '⬜'}
                                </span>
                                <div className="space-y-1 min-w-0">
                                    <div className={`text-sm font-bold leading-snug ${selectedTask?.id === task.id ? 'text-white' : 'text-slate-200 group-hover:text-white'
                                        }`}>
                                        {task.title}
                                    </div>
                                    {task.description && (
                                        <div className={`text-xs line-clamp-1 ${selectedTask?.id === task.id ? 'text-blue-100' : 'text-slate-500'
                                            }`}>
                                            {task.description}
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 mt-2">
                                        {task.actor && (
                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${selectedTask?.id === task.id
                                                    ? 'bg-white/20 text-white border-white/20'
                                                    : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                                                }`}>
                                                {task.actor}
                                            </span>
                                        )}
                                        {task.assignee && (
                                            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${selectedTask?.id === task.id
                                                    ? 'bg-white/20 text-white'
                                                    : 'bg-blue-500/10 text-blue-400'
                                                }`}>
                                                @{task.assignee}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-slate-700/50 flex justify-between items-center bg-slate-900/50 shrink-0">
                <div className="text-[10px] font-mono text-slate-500">{completedCount}/{phase.tasks.length} ({percent}%)</div>
            </div>
        </>
    )
}


function TaskListView({ tasks, onTaskClick }: {
    tasks: (MarkdownTask & { assignee?: string })[]
    onTaskClick: (task: MarkdownTask & { assignee?: string }) => void
}) {
    // Group by project and then by phase
    const groupedTasks = tasks.reduce((acc, task) => {
        const project = task.project || 'Root'
        const phase = task.phase || 'General'
        if (!acc[project]) acc[project] = {}
        if (!acc[project][phase]) acc[project][phase] = []
        acc[project][phase].push(task)
        return acc
    }, {} as Record<string, Record<string, (MarkdownTask & { assignee?: string })[]>>)

    return (
        <div className="h-full overflow-y-auto space-y-8 pr-1">
            {Object.entries(groupedTasks).map(([project, phases]) => {
                const projectTasks = Object.values(phases).flat()
                const completedCount = projectTasks.filter(t => t.status === 'done').length
                const percent = Math.round((completedCount / projectTasks.length) * 100)

                return (
                    <div key={project} className="space-y-4">
                        <div className="flex items-center justify-between px-2">
                            <div className="flex items-center gap-3">
                                <span className="text-xl">📁</span>
                                <h2 className="text-lg font-black text-white uppercase tracking-tighter">{project}</h2>
                                <span className="px-2 py-0.5 rounded-full bg-slate-800 text-[10px] font-bold text-slate-500 border border-slate-700">
                                    {projectTasks.length} TASKS
                                </span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] font-mono text-slate-500">
                                <span>{percent}% COMPLETE</span>
                                <div className="w-20 h-1 bg-slate-800 rounded-full overflow-hidden">
                                    <div className="h-full bg-green-500" style={{ width: `${percent}%` }} />
                                </div>
                            </div>
                        </div>

                        <div className="space-y-6">
                            {Object.entries(phases).map(([phase, tasksInPhase]) => (
                                <div key={phase} className="bg-slate-800/30 rounded-2xl border border-slate-700/50 overflow-hidden backdrop-blur-sm">
                                    <div className="px-5 py-3 bg-slate-800/50 border-b border-slate-700/50 flex items-center gap-3">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-[0_0_8px_rgba(245,158,11,0.5)]" />
                                        <h3 className="font-bold text-slate-200 text-xs uppercase tracking-widest">{phase}</h3>
                                        <span className="text-[10px] text-slate-500 font-medium ml-auto">{tasksInPhase.length} tasks</span>
                                    </div>
                                    <div className="divide-y divide-slate-700/30">
                                        {tasksInPhase.map(task => (
                                            <TaskListItem key={task.id} task={task} onClick={() => onTaskClick(task)} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )
            })}
        </div>
    )
}

function TaskListItem({ task, onClick }: {
    task: MarkdownTask & { assignee?: string };
    onClick: () => void
}) {
    const statusConfig = columns.find(c => c.id === task.status)
    const statusIcons: Record<TaskStatus, string> = {
        todo: '⬜',
        in_progress: '🔄',
        done: '✅',
    }

    return (
        <button
            onClick={onClick}
            className={`w-full group flex items-center gap-4 py-3 px-5 text-sm text-left hover:bg-slate-700/30 transition-colors ${task.status === 'done' ? 'bg-slate-900/10' : ''
                }`}
        >
            <div className="flex-shrink-0 text-lg">
                <span title={statusConfig?.title}>{statusIcons[task.status]}</span>
            </div>

            <div className="flex-1 min-w-0">
                <div className={`font-medium ${task.status === 'done' ? 'text-slate-500 line-through' : 'text-slate-200'} truncate group-hover:text-white transition-colors`}>
                    {task.title}
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                    <span className="truncate">📄 {task.docPath.split('/').pop()}</span>
                    {task.phase && (
                        <>
                            <span>•</span>
                            <span className="text-amber-500/80 font-medium">{task.phase}</span>
                        </>
                    )}
                </div>
            </div>

            <div className="flex-shrink-0 flex items-center gap-3">
                {task.assignee && (
                    <span className="text-[10px] text-blue-400 font-medium px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20">
                        @{task.assignee}
                    </span>
                )}
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${task.status === 'done' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                    task.status === 'in_progress' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                        'bg-slate-700/50 text-slate-400 border-slate-600/50'
                    }`}>
                    {statusConfig?.title}
                </span>
            </div>
        </button>
    )
}

function TaskDetailModal({ task, onClose }: {
    task: MarkdownTask & { assignee?: string }
    onClose: () => void
}) {
    const { updateMarkdownTaskStatus, isAuthenticated, user } = useAppStore()
    const [status, setStatus] = useState<TaskStatus>(task.status)
    const [assignee, setAssignee] = useState(task.assignee || '')
    const [isSaving, setIsSaving] = useState(false)

    const handleSave = async () => {
        if (!isAuthenticated) return
        setIsSaving(true)
        try {
            await updateMarkdownTaskStatus(task.id, {
                status,
                assignee: assignee || undefined,
            })
            onClose()
        } catch (error) {
            console.error('Failed to save:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleAssignToMe = () => {
        if (user?.login) {
            setAssignee(user.login)
        }
    }

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in" onClick={onClose}>
            <div
                className="bg-slate-900 rounded-2xl border border-slate-800 w-full max-w-2xl shadow-2xl overflow-hidden scale-in"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-800 flex items-start justify-between bg-slate-800/20">
                    <div className="flex-1 pr-6">
                        <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-mono uppercase tracking-widest text-slate-500 bg-slate-800 px-2 py-1 rounded">
                                {task.project || task.section}
                            </span>
                            {task.phase && (
                                <span className="text-xs font-mono uppercase tracking-widest text-amber-500 bg-amber-500/10 px-2 py-1 rounded border border-amber-500/20">
                                    {task.phase}
                                </span>
                            )}
                            <span className="text-xs font-mono text-slate-600">ID: {task.id.split(':').pop()}</span>
                        </div>
                        <h2 className="text-xl font-bold text-white leading-tight">{task.title}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-500 hover:text-white transition-colors p-2 hover:bg-slate-800 rounded-full"
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className="px-8 py-6 space-y-8 overflow-y-auto max-h-[70vh]">
                    {/* Description */}
                    {task.description && (
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Description</label>
                            <div className="text-slate-300 leading-relaxed bg-slate-800/30 p-4 rounded-xl border border-slate-800 prose prose-invert prose-sm max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {task.description}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Status */}
                        <div>
                            <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Status</label>
                            <div className="flex flex-col gap-2">
                                {columns.map(col => (
                                    <button
                                        key={col.id}
                                        onClick={() => isAuthenticated && setStatus(col.id)}
                                        disabled={!isAuthenticated}
                                        className={`flex items-center gap-3 py-3 px-4 rounded-xl text-sm font-medium transition-all text-left ${status === col.id
                                            ? `${col.color} text-white shadow-lg shadow-blue-500/10`
                                            : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                                            } ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        <span className={`w-2 h-2 rounded-full ${status === col.id ? 'bg-white' : col.color}`} />
                                        {col.title}
                                        {status === col.id && <span className="ml-auto">✓</span>}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Assignee */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Assignee</label>
                                <div className="space-y-3">
                                    <input
                                        type="text"
                                        value={assignee}
                                        onChange={(e) => setAssignee(e.target.value)}
                                        placeholder="GitHub username"
                                        disabled={!isAuthenticated}
                                        className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500 transition-colors disabled:opacity-50"
                                    />
                                    {isAuthenticated && user && (
                                        <button
                                            onClick={handleAssignToMe}
                                            className="w-full py-2 px-4 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-slate-400 text-xs font-medium transition-colors"
                                        >
                                            Assign to me (@{user.login})
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Links */}
                            <div className="space-y-3">
                                <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Links</label>
                                <Link
                                    to="/docs/$"
                                    params={{ _splat: task.docPath }}
                                    className="flex items-center gap-3 p-3 bg-blue-500/5 hover:bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 hover:text-blue-300 transition-all group"
                                    onClick={onClose}
                                >
                                    <span className="text-xl">📄</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-medium truncate">{task.docPath.split('/').pop()}</div>
                                        <div className="text-[10px] text-slate-500 group-hover:text-slate-400 transition-colors">Documentation • Line {task.line}</div>
                                    </div>
                                </Link>

                                {task.githubUrl && (
                                    <a
                                        href={task.githubUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-3 p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-xl text-slate-300 hover:text-white transition-all group"
                                    >
                                        <span className="text-xl">🐙</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm font-medium">View on GitHub</div>
                                            <div className="text-[10px] text-slate-500 group-hover:text-slate-400 transition-colors">Open source file at this line</div>
                                        </div>
                                        <span className="text-xs opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-6 border-t border-slate-800 flex justify-end items-center gap-4 bg-slate-800/10">
                    {!isAuthenticated && (
                        <div className="text-[10px] text-amber-500 bg-amber-500/10 px-3 py-1.5 rounded-full border border-amber-500/20 mr-auto font-medium">
                            Only maintainers can update tasks.
                        </div>
                    )}
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition-all text-sm font-medium"
                    >
                        Close
                    </button>
                    {isAuthenticated && (
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="px-8 py-2.5 bg-blue-600 hover:bg-blue-500 shadow-xl shadow-blue-500/20 rounded-xl text-white font-bold transition-all disabled:opacity-50 text-sm active:scale-95"
                        >
                            {isSaving ? 'Saving Changes...' : 'Save Changes'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    )
}
